import {useTranslation} from '@aboutyou/i18n';
import {
  AdjustTracker,
  useTrackAdjustImpression,
  useTrackFeature,
} from '@aboutyou/tracking';
import {
  DevicesImage,
  GenericIconName,
  ModalDialog,
  SlideInContainer,
  desktopAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useEffect, useMemo} from 'react';
import {CouponInfoContentPopper} from '../Coupons/CouponInfoContentPopper/CouponInfoContentPopper';
import {useCouponInfoContentPopperStateManager} from '../Coupons/CouponInfoContentPopper/useCouponInfoContentPopperStateManager';
import bannerBackground from '../Coupons/assets/bannerBackground.svg';
import {StoreBadges} from '../StoreBadges/StoreBadges';
import {AdjustDeepLink} from '../StoreBadges/utils/useGetStoreLink';
import {
  WEB_COUPON_DISCOUNT,
  WebCouponBanner,
  WebCouponTranslations,
} from '../WebCouponBanner/WebCouponBanner';
import qrcode from './assets/qrcode.svg';
import {useAutoOpenSlideInWebCouponBanner} from './utils/useAutoOpenSlideInWebCouponBanner';
import {useUpdateScrollToTopOffset} from './utils/useUpdateScrollToTopOffset';

const TRACK_FEATURE_LABEL = 'generic_coupon_app_banner_any_desk';

export const SlideInWebCouponBanner: React.FC = () => {
  const {t} = useTranslation();
  const stateManagerCustomKey = 'slideInWebCouponBanner';
  const {open: openInfoDialog} = useCouponInfoContentPopperStateManager(
    stateManagerCustomKey,
  );
  const {isOpen: shouldAutoOpen} = useAutoOpenSlideInWebCouponBanner();
  useUpdateScrollToTopOffset();

  const trackFeature = useTrackFeature();
  const adjustTracker = AdjustTracker.DESK_WEB_COUPON_BANNER;
  const trackAdjustImpression = useTrackAdjustImpression({adjustTracker});

  const onStoreBadgeClick = () => {
    trackFeature({eventType: 'click', label: TRACK_FEATURE_LABEL});
  };

  const onSlideInToggle = (state: boolean) => {
    trackFeature({
      eventType: state === true ? 'open' : 'close',
      label: TRACK_FEATURE_LABEL,
    });
  };

  useEffect(() => {
    if (shouldAutoOpen) {
      trackFeature({eventType: 'impression', label: TRACK_FEATURE_LABEL});

      trackAdjustImpression();
    }
  }, [shouldAutoOpen]);

  const translations: WebCouponTranslations = useMemo(
    () => ({
      text: t(
        'Get your <b>{discount} Welcome Coupon for new App customers</b> and many personalized offers.',
        {
          discount: WEB_COUPON_DISCOUNT,
        },
      ),
      modalBadgeText: t('Coupons'),
      modalTitle: t('Welcome to the App Coupon'),
      modalContent: t('WebCouponBannerInfoText'),
      closeButton: t('Close'),
    }),
    [t],
  );

  return (
    <>
      <SlideInContainer
        data-testid="slideInWebCouponBanner"
        expanded={shouldAutoOpen}
        onToggle={onSlideInToggle}
        headerProps={{
          text: t('App exclusive Coupons for you!'),
          iconName: GenericIconName.Coupons,
          backgroundColor: 'PrimaryCoupon',
        }}
        onInfoIconClick={openInfoDialog}
      >
        <StyledWebCouponBanner
          deviceSize={180}
          translations={translations}
          textSize="px14"
          showInfoIcon={false}
          qrCodeImage={qrcode}
          bannerBackgroundSvg={bannerBackground}
          StoreBadgesComponent={
            <StyledStoreBadges
              dataTestId="slideInWebCouponBannerStoreBadges"
              height={24}
              adjustTracker={adjustTracker}
              deepLink={AdjustDeepLink.CouponWallet}
              onClick={onStoreBadgeClick}
            />
          }
        />
      </SlideInContainer>

      <CouponInfoContentPopper
        stateManagerCustomKey={stateManagerCustomKey}
        LayoutComponent={ModalDialog}
        translations={{
          badgeText: translations.modalBadgeText,
          title: translations.modalTitle,
          content: translations.modalContent,
          closeButtonText: translations.closeButton,
        }}
        data-testid="webCouponBannerInfoComponent"
      />
    </>
  );
};

const StyledWebCouponBanner = styled(WebCouponBanner)`
  max-width: 67vw;
  padding: 0 12px;

  ${DevicesImage} {
    position: relative;
    top: 12px;
  }

  ${desktopAndUpLinaria(`
    max-width: min(60vw, 560px);
    padding: 0 20px;

    ${DevicesImage} {
      top: 20px;
    }
  `)}
`;

const StyledStoreBadges = styled(StoreBadges)`
  margin: 12px 0 0 -4px;
`;
